/* Marking kit returned — STORY-030.
 *
 * ── ONE control, used by all three screens ──────────────────────────────────
 * The still-out screen, an Event's loadout, and the Event's own equipment
 * section all mark things returned, and the story is explicit that they must
 * ask the same question in the same words. Three entry points that behaved
 * differently meant the condition was captured or lost depending on which
 * screen somebody happened to be on — found 2026-08-11 by marking one item
 * returned from three places and getting three different outcomes.
 *
 * So the follow-up lives here, not in a screen. A copy of it anywhere else is
 * the defect.
 *
 * ── the condition question is offered, never required ───────────────────────
 * Requiring it is exactly the friction that left the reference workbook's
 * equipment sheet empty. The return has already happened by the time the
 * question appears; answering it is a second, optional write.
 */

import { ITEM_CONDITIONS, type ItemCondition } from '@eventuley/contracts';
import type { MessageKey } from '@eventuley/i18n';
import { useId, useState } from 'react';
import { NetworkError } from '../../../shared/api/client.js';
import { useTranslate } from '../../../shared/i18n/index.js';
import { Button, Callout } from '../../../shared/ui/index.js';
import { useReturnEquipment } from '../api/index.js';

export const CONDITION_LABELS = {
  excellent: 'inventory.condition.excellent',
  good: 'inventory.condition.good',
  fair: 'inventory.condition.fair',
  lightly_damaged: 'inventory.condition.lightly_damaged',
  problematic: 'inventory.condition.problematic',
  lost: 'inventory.condition.lost',
} as const satisfies Record<ItemCondition, MessageKey>;

export function ReturnControl({
  organisationId,
  assignmentIds,
  label,
  onReturned,
  onDone,
  startAsking,
}: {
  readonly organisationId: string;
  /** One, or a whole loadout — packing down happens at once. */
  readonly assignmentIds: readonly string[];
  readonly label: string;
  /**
   * The kit is back. Fires the moment the write lands, before the question.
   *
   * The still-out screen uses this to HOLD the row: the list is "what has not
   * come back", so it stops qualifying immediately — but if it left immediately
   * it would take its unanswered question with it, which is how that screen
   * could never capture a condition while the loadout panel could.
   */
  readonly onReturned?: () => void;
  /** The follow-up is answered or dismissed. Now the row may go. */
  readonly onDone?: () => void;
  /**
   * Open on the question, because the return already happened.
   *
   * A held row is rendered by a DIFFERENT instance of this control from the one
   * that recorded the return — the row moved list, so the first instance
   * unmounted and its question went with it. That is precisely the defect the
   * story records finding on 2026-08-11, reproduced by the move rather than by
   * the screens disagreeing. Carrying the state in a prop is what survives the
   * remount.
   */
  readonly startAsking?: boolean;
}) {
  const t = useTranslate();
  const mark = useReturnEquipment(organisationId);
  const [asking, setAsking] = useState(startAsking ?? false);
  const conditionId = useId();

  if (assignmentIds.length === 0) return null;

  if (asking) {
    return (
      <Callout tone="info">
        <p>{t('inventory.return.followup')}</p>
        <div className="field">
          <label className="field__label" htmlFor={conditionId}>
            {t('inventory.condition_label')}
          </label>
          <select
            id={conditionId}
            className="field__control"
            defaultValue=""
            onChange={(changed) => {
              if (changed.target.value === '') return;
              /* A second, optional write. The return already happened. */
              mark.mutate(
                {
                  assignmentIds: [...assignmentIds],
                  condition: changed.target.value as ItemCondition,
                },
                {
                  onSuccess: () => {
                    setAsking(false);
                    onDone?.();
                  },
                },
              );
            }}
          >
            <option value="">{t('inventory.condition_unchanged')}</option>
            {ITEM_CONDITIONS.map((condition) => (
              <option key={condition} value={condition}>
                {t(CONDITION_LABELS[condition])}
              </option>
            ))}
          </select>
        </div>
        <div className="btn-row">
          {/* Dismissing is a first-class answer, not a cancel. Nothing is
              undone by it — the kit is back either way. */}
          <Button
            variant="ghost"
            onClick={() => {
              setAsking(false);
              onDone?.();
            }}
          >
            {t('inventory.return.skip')}
          </Button>
        </div>
      </Callout>
    );
  }

  return (
    <>
      <Button
        variant="secondary"
        busy={mark.isPending}
        onClick={() =>
          mark.mutate(
            { assignmentIds: [...assignmentIds] },
            /* The follow-up opens on success, so the return is recorded before
               anybody is asked anything. A question that has to be answered
               for the write to land is the friction this avoids. */
            {
              onSuccess: () => {
                setAsking(true);
                onReturned?.();
              },
            },
          )
        }
      >
        {label}
      </Button>
      {mark.error ? (
        <Callout tone="danger">
          {mark.error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
        </Callout>
      ) : null}
    </>
  );
}
